import { useMutation } from '@tanstack/react-query';
import { v4 as uuidv4 } from 'uuid';
import {
  QuoteDTO,
  QuoteSectionDTO,
  createQuoteV2SectionMutation
} from '@setvi/shared/services/react-query';
import { moveQuoteV2ItemsMutation } from 'Services/Mutations/QuotesV2';
import { generateKeyBetween } from 'fractional-indexing';
import {
  moveItemToSectionOptimistically,
  generateNextPosition,
  updateItemInTree
} from '../utils/quote-helpers';
import { useQuoteCache } from './use-quote-cache';

interface AddSectionAndMoveItemVariables {
  name: string;
  description?: string;
  itemId: string;
}

/**
 * Add section and move item to it optimistically
 */
const addSectionAndMoveItemOptimistically = (
  quote: QuoteDTO | undefined,
  { name, description, itemId }: AddSectionAndMoveItemVariables,
  sectionPosition: string,
  tempSectionId: string
): QuoteDTO | undefined => {
  if (!quote) return quote;

  const newSection = {
    id: tempSectionId,
    type: 'section',
    name,
    note: description || null,
    children: [],
    position: sectionPosition
  } as unknown as QuoteSectionDTO;

  const quoteWithNewSection: QuoteDTO = {
    ...quote,
    items: [...quote.items, newSection]
  };

  return moveItemToSectionOptimistically(
    quoteWithNewSection,
    itemId,
    tempSectionId
  );
};

/**
 * Add section and move item to it using v2 endpoints
 * Creates section via v2 section endpoint, then moves item via v2 move endpoint
 * Section appears immediately with the item inside it
 */
export const useAddSectionAndMoveItem = (quoteId: string) => {
  const cache = useQuoteCache(quoteId);
  const { mutationFn: createSection } = createQuoteV2SectionMutation();
  const { mutationFn: moveItems } = moveQuoteV2ItemsMutation();

  return useMutation({
    mutationFn: async ({
      name,
      description,
      itemId
    }: AddSectionAndMoveItemVariables) => {
      const items = cache.getItems() || [];
      const position = generateNextPosition(items);

      const sectionResponse = await createSection({
        quoteId,
        name,
        note: description || '',
        position
      });

      const newSectionId =
        (sectionResponse as any)?.item?._id || (sectionResponse as any)?._id;
      if (newSectionId) {
        await moveItems({
          quoteId,
          moves: [
            {
              itemId,
              targetSectionId: newSectionId,
              position: generateKeyBetween(null, null)
            }
          ]
        });
      }

      return sectionResponse;
    },

    onMutate: async (variables: AddSectionAndMoveItemVariables) => {
      await cache.cancel();

      const previousQuote = cache.getQuote();
      const previousItems = cache.getItems();
      const tempSectionId = uuidv4();

      // Compute section position from pre-optimistic data
      const sectionPosition = generateNextPosition(previousItems || []);

      const composed = previousQuote
        ? ({
            ...previousQuote,
            items: previousItems || []
          } as unknown as QuoteDTO)
        : undefined;

      const updated = addSectionAndMoveItemOptimistically(
        composed,
        variables,
        sectionPosition,
        tempSectionId
      );

      if (updated) {
        const { items: updatedItems, ...quoteData } = updated as any;
        cache.setQuote(() => quoteData);
        cache.setItems(() => updatedItems);
      }

      return { previousQuote, previousItems, tempSectionId };
    },

    onError: (_error, _variables, context) => {
      if (context?.previousQuote) {
        cache.setQuote(() => context.previousQuote);
      }
      if (context?.previousItems) {
        cache.setItems(() => context.previousItems);
      }
    },

    onSuccess: (data, _variables, context) => {
      const res = data as any;
      const serverId = res?.item?._id || res?._id;
      if (serverId && context?.tempSectionId) {
        cache.setItems(old =>
          old
            ? updateItemInTree(old, context.tempSectionId, {
                _id: serverId,
                id: serverId
              })
            : old
        );
      }
      cache.invalidate();
    }
  });
};
